import { Transition } from '@headlessui/react'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import { tm } from '@captaincause/ui/tm'
import blossom from 'assets/hero-dift-blossom.webp'
import cloud from 'assets/hero-dift-cloud.webp'
import enveloppe from 'assets/hero-dift-dift-eveloppe.webp'
import dift from 'assets/hero-dift-dift.webp'
import hues from 'assets/hero-dift-hues.webp'
import minty from 'assets/hero-dift-minty.webp'
import nextmove from 'assets/hero-dift-nextmove.webp'
import highlightLg from 'assets/home-highlight-lg.svg'
import { Button } from 'components/Button'

const OPERATION_DISPLAY_DURATION = 3000
const OPERATION_SWITCH_DELAY = 100

const OPERATIONS = [minty, blossom, nextmove, cloud, hues]

function B2B() {
  const leaveTimeout = useRef<NodeJS.Timeout>()
  const enterTimeout = useRef<NodeJS.Timeout>()

  const isOperationsPlaying = useRef(false)

  const [currentOperation, setCurrentOperation] = useState<number>(0)

  useEffect(
    () => () => {
      clearTimeout(leaveTimeout.current)
      clearTimeout(enterTimeout.current)
    },
    [],
  )

  return (
    <>
      <div className="flex items-center justify-center lg:hidden">
        <Image
          src={minty}
          alt=""
          priority
          quality={100}
          className="w-[450px] rounded-xl shadow-lg"
        />
      </div>
      <div className="hidden items-center justify-center pb-10 md:flex md:pb-0 lg:w-[410px] lg:min-w-[410px] xl:w-[610px] xl:min-w-[610px]">
        {OPERATIONS.map((operation, index) => (
          <Transition
            key={operation.src}
            appear={index === 0 && isOperationsPlaying.current === false}
            show={index === currentOperation}
            as="div"
            className="relative h-full w-[450px] transition-transform ease-out data-[closed]:opacity-0 lg:h-[270px] lg:w-[410px] xl:h-full xl:w-[610px]"
            afterEnter={() => {
              if (isOperationsPlaying.current === false) {
                isOperationsPlaying.current = true
              }
              enterTimeout.current = setTimeout(() => {
                setCurrentOperation(-1)
              }, OPERATION_DISPLAY_DURATION)
            }}
            afterLeave={() => {
              leaveTimeout.current = setTimeout(() => {
                setCurrentOperation(v => {
                  if (index === OPERATIONS.length - 1) return 0

                  return index + 1
                })
              }, OPERATION_SWITCH_DELAY)
            }}
          >
            <Image
              src={operation}
              alt=""
              priority
              quality={100}
              className="absolute h-full w-auto rounded-xl shadow-sm"
            />
          </Transition>
        ))}
      </div>
    </>
  )
}

function C2C() {
  return (
    <>
      <div className="relative flex items-center justify-center pb-10 md:pb-0 lg:hidden">
        <Image
          src={dift}
          alt=""
          priority
          quality={100}
          className={tm('h-[350px] w-auto')}
        />
      </div>
      <div className="relative hidden items-center justify-start pb-10 md:pb-0 lg:flex lg:w-[410px] lg:min-w-[410px] xl:w-[610px] xl:min-w-[610px]">
        <Image
          src={dift}
          alt=""
          priority
          quality={100}
          className={tm('absolute left-0 top-0 h-full w-auto')}
        />

        <Image
          src={enveloppe}
          alt=""
          priority
          quality={100}
          className={tm(
            'absolute top-0 hidden h-full w-auto lg:left-[100%] lg:block xl:left-[70%]',
          )}
        />
      </div>
    </>
  )
}

type Mode = 'b2b' | 'c2c'

const MODES = {
  b2b: {
    Component: B2B,
    toggleLink: 'b2b.toggle.link',
    description: 'b2b.description',
    link: 'b2b.cta.link',
  },
  c2c: {
    Component: C2C,
    toggleLink: 'c2c.toggle.link',
    description: 'c2c.description',
    link: 'c2c.cta.link',
  },
} as const

export interface HeroDiftProps {
  mode: Mode | null
}

export default function HeroDift({ mode: propsMode }: HeroDiftProps) {
  const { t } = useTranslation('hero-dift.block')

  const mode = propsMode ?? 'b2b'

  const MODE = MODES[mode]

  return (
    <div className="h-auto overflow-hidden bg-light-beige">
      <div
        className={tm(
          'relative mx-auto flex min-h-[550px] max-w-[1400px] flex-col justify-center gap-x-20 px-8 py-10 lg:flex-row lg:px-14 lg:py-16',
        )}
      >
        <div
          className={tm(
            'flex w-full flex-col items-center justify-center pb-10 lg:pb-0',
            mode === 'b2b' && 'xl:w-[440px]',
          )}
        >
          <div className="relative pb-4">
            <h1 className="text-center font-default text-6xl font-semibold -tracking-[1px] md:whitespace-nowrap [&_*]:font-default">
              {t('title')}
              <div className="relative inline-block">
                <Image
                  src={highlightLg}
                  alt=""
                  className="absolute left-1/2 top-1/2 hidden max-w-none -translate-x-1/2 -translate-y-1/2 md:block md:h-[86px] md:w-[150px]"
                />
                <span className="z-1 relative">{t('title-2')}</span>
              </div>
            </h1>
          </div>
          <div className="w-full pb-10 text-center text-lg leading-8 text-dark-gray lg:w-[460px]">
            {t(MODE.description)}
          </div>
          <div
            className={tm(
              'flex justify-center gap-x-6',
              mode === 'c2c' && 'flex-wrap gap-y-2 lg:flex-nowrap',
            )}
          >
            <Button
              href={t(MODE.link)}
              variant="primary"
              small
              className="h-12 w-[240px] whitespace-nowrap rounded-full px-7 py-3 font-semibold lg:h-13"
            >
              {t('cta')}
            </Button>
            {mode === 'c2c' ? (
              <Button
                href={t('c2c.cta2.link')}
                variant="secondary"
                small
                className="h-12 w-[240px] whitespace-nowrap rounded-full px-7 py-3 font-semibold lg:h-13"
              >
                {t('c2c.cta2')}
              </Button>
            ) : null}
          </div>
        </div>
        <MODE.Component />
      </div>
    </div>
  )
}
